<template>
    <div
        class="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:px-6 lg:px-8"
    >
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <a href="/">
                <svg-vue
                    class="mx-auto h-12 w-12"
                    icon="logo-leaf"
                    alt="Nutreance"
                />
            </a>

            <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
                Sign In
            </h2>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <StatusMessage
                    v-if="hasError(errors, 'email')"
                    :initialMessage="error(errors, 'email')"
                    type="error"
                />

                <form
                    ref="form"
                    action="/login"
                    method="POST"
                    @submit.prevent="onSubmit(form)"
                    autocomplete="on"
                >
                    <input
                        v-if="userFlow"
                        type="hidden"
                        name="_token"
                        :value="csrf"
                    />
                    <input
                        v-if="userFlow"
                        type="hidden"
                        name="remember"
                        value="1"
                    />

                    <div class="mt-3 mb-6">
                        <label
                            for="email"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Email
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input
                                v-model="inputs.email"
                                ref="email"
                                id="email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                required
                                :class="[
                                    errorMessage
                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                ]"
                                :aria-invalid="errorMessage ? 'true' : 'false'"
                                :aria-describedby="
                                    errorMessage ? 'email-error' : ''
                                "
                            />
                            <div
                                v-if="errorMessage"
                                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                            >
                                <ExclamationCircleIcon
                                    class="h-5 w-5 text-red-500"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <p
                            v-if="errorMessage"
                            class="mt-2 text-sm text-red-600"
                            id="email-error"
                        >
                            {{ errorMessage }}
                        </p>
                    </div>

                    <div v-if="userFlow" class="my-6">
                        <label
                            for="password"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Password
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autocomplete="current-password"
                                required
                                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-nutreance-green focus:border-nutreance-green sm:text-sm"
                            />
                        </div>
                    </div>

                    <div class="my-6">
                        <button
                            :name="userFlow ? 'login' : 'continue'"
                            type="submit"
                            class="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                        >
                            {{ userFlow ? "Sign In" : "Continue" }}
                            <LoadingIcon
                                v-if="submitted"
                                class="h-6 w-6 ml-2"
                            />
                            <ArrowSmRightIcon
                                v-else
                                class="h-6 w-6 ml-2"
                                aria-hidden="true"
                            />
                        </button>
                    </div>

                    <div v-if="userFlow" class="mt-6 mb-3 text-sm text-center">
                        <a
                            href="/password/reset"
                            class="text-gray-400 hover:text-gray-500"
                        >
                            Forgot your password?
                        </a>
                    </div>
                </form>
            </div>
        </div>

        <p class="mt-4 text-center text-sm text-gray-400">
            Don't have an account?

            <a
                href="/register"
                class="ml-1 font-medium text-nutreance-green hover:text-nutreance-green-lighter"
            >
                Sign Up
            </a>
        </p>
    </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { ArrowSmRightIcon, ExclamationCircleIcon } from "@heroicons/vue/solid";
import StatusMessage from "../StatusMessage.vue";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";
import { loadLoginFlow } from "../../api/auth";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    old?: any;
}>();

const { error, hasError, oldInput, submitted } = useForm();
const email = ref<any>(null);
const form = ref<any>(null);
const userFlow = ref(Object.keys(props.errors).length > 0);
const errorMessage = ref<string | null>(null);

const inputs = reactive({
    email: oldInput(props.old, "email") ? oldInput(props.old, "email") : null,
});

function onSubmit(form: any) {
    if (submitted.value) {
        return;
    }

    submitted.value = true;

    if (userFlow.value) {
        form.submit();
        return;
    }

    loadLoginFlow({
        email: inputs.email,
    })
        .then((data: any) => {
            if (data.userFlow) {
                submitted.value = false;
                userFlow.value = data.userFlow;
            } else {
                window.location.href = `/login-link/create/${inputs.email}`;
            }
        })
        .catch((error: any) => {
            submitted.value = false;
            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                errorMessage.value = error.response.data.errors[field][0];
            }
        });
}

onMounted(() => email.value.focus());
</script>
